import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { empleadosAPI, textoErrorApi } from '../api/api';
import {
  Users, Clock, Sun, AlertTriangle, TrendingUp,
  Check, X, RefreshCw, Timer,
  Info, Loader2, Plus, Trash2,
  Lock, CalendarX, Activity,
  Search, ChevronDown, Eye, User, Calendar, FileText
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import { useAuth } from '../context/AuthContext';
import { revisarNovedades } from '../components/guia/novedades';
import RegistroHorarioEmpleado, { useAvisoTemporal, AvisoInline, DialogoConfirmar } from '../components/empleados/RegistroHorarioEmpleado';

// Petición al API (axios con el token del interceptor); devuelve los datos
const pedir = async (url, { method = 'get', data, params } = {}) => {
  const res = await api.request({ url, method, data, params });
  return res.data;
};

const fmtTime = (dt) => dt ? new Date(dt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '--:--';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
// "2026-10-06" -> "lun, 6 oct" sin desplazar el día por la zona horaria
const fmtDia = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
const elapsed = (dt) => {
  if (!dt) return '';
  const diff = Date.now() - new Date(dt).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Get current week range (Monday-based)
const getWeekRange = () => {
  const hoy = new Date();
  const day = hoy.getDay();
  const daysToMon = day === 0 ? 6 : day - 1;
  const ini = new Date(hoy);
  ini.setDate(hoy.getDate() - daysToMon);
  return {
    start: ini.toISOString().split('T')[0],
    end: hoy.toISOString().split('T')[0]
  };
};

// Estadísticas de fichaje de un empleado a partir de sus registros del mes
const calcularStats = (emp, regs) => {
  const week = getWeekRange();
  let horasMes = 0, diasMes = 0;
  regs.forEach(reg => {
    if (reg.minutos_trabajados) { horasMes += reg.minutos_trabajados / 60; diasMes++; }
  });
  const regsSemana = regs.filter(reg => reg.fecha >= week.start && reg.fecha <= week.end);
  let horasSemana = 0, diasSemana = 0;
  regsSemana.forEach(reg => {
    if (reg.minutos_trabajados) { horasSemana += reg.minutos_trabajados / 60; diasSemana++; }
  });
  const contrato = emp.horas_contrato_semana || 40;
  // Horas de contrato del mes y avance esperado a fecha de hoy
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayOfMonth = now.getDate();
  const contratoMes = (contrato / 7) * daysInMonth;
  const pctMes = Math.min((horasMes / contratoMes) * 100, 150);
  const pctMesEsperado = (dayOfMonth / daysInMonth) * 100;
  return {
    horasSemana: +horasSemana.toFixed(1),
    diasSemana,
    horasMes: +horasMes.toFixed(1),
    diasMes,
    contrato,
    contratoMes: +contratoMes.toFixed(1),
    pctSemana: Math.min((horasSemana / contrato) * 100, 100),
    pctMes: +pctMes.toFixed(1),
    pctMesEsperado: +pctMesEsperado.toFixed(1),
    exceso: horasSemana > contrato,
    registros: regs,
  };
};

const statsVacias = (emp) => ({ horasSemana: 0, diasSemana: 0, horasMes: 0, diasMes: 0, contrato: emp.horas_contrato_semana || 40, contratoMes: 0, pctSemana: 0, pctMes: 0, pctMesEsperado: 0, exceso: false, registros: [] });

// Días naturales entre dos fechas AAAA-MM-DD, ambas incluidas
const diasEntre = (inicio, fin) => Math.floor((new Date(fin) - new Date(inicio)) / (1000 * 60 * 60 * 24)) + 1;

const TIPOS_AUSENCIA = [
  { valor: 'vacaciones', texto: 'Vacaciones' },
  { valor: 'asuntos_propios', texto: 'Asuntos propios' },
  { valor: 'baja_medica', texto: 'Baja médica' },
  { valor: 'permiso', texto: 'Permiso' },
  // Solo dirección lo registra: no descuenta vacaciones, queda aprobado y sale
  // en amarillo en el calendario (ausencias_calendario.py)
  { valor: 'dia_ocupado', texto: 'Día ocupado', opcion: 'Día ocupado (médico, curso, trámite...)', soloDireccion: true },
];

// Motivos del día ocupado (quién los ve lo decide el servidor: la persona y dirección)
const MOTIVOS_OCUPADO = [
  { valor: 'medico', texto: 'Médico', opcion: 'Médico' },
  { valor: 'curso', texto: 'Curso', opcion: 'Curso o formación' },
  { valor: 'tramite', texto: 'Trámite', opcion: 'Trámite o gestión' },
  { valor: 'otro', texto: 'Otro', opcion: 'Otro' },
];

// Las citas médicas antiguas ya llegan del servidor como día ocupado de médico
const esDiaOcupado = (sol) => sol?.tipo === 'dia_ocupado' || sol?.tipo === 'cita_medica';
const textoMotivoOcupado = (sol) => {
  if (sol?.tipo === 'cita_medica') return 'Médico';
  const motivo = MOTIVOS_OCUPADO.find((m) => m.valor === sol?.motivo_ocupado);
  if (motivo && motivo.valor !== 'otro') return motivo.texto;
  return String(sol?.motivo_texto || '').trim();
};
// "Día ocupado · Curso"; el resto, el nombre del tipo
const textoTipo = (sol) => {
  if (esDiaOcupado(sol)) {
    const motivo = textoMotivoOcupado(sol);
    return motivo ? `Día ocupado · ${motivo}` : 'Día ocupado';
  }
  return TIPOS_AUSENCIA.find((t) => t.valor === sol?.tipo)?.texto || sol?.tipo || 'Ausencia';
};

// Eventos confirmados (cabecera o jornadas) que caen entre dos fechas
const eventosEnRango = (presupuestos, fechaInicio, fechaFin) => {
  const inicio = String(fechaInicio || '').slice(0, 10);
  const fin = String(fechaFin || '').slice(0, 10);
  const encontrados = [];
  const lista = Array.isArray(presupuestos) ? presupuestos : (presupuestos?.presupuestos || []);
  lista.forEach((p) => {
    const fechas = (p.jornadas && p.jornadas.length > 0)
      ? p.jornadas.map((j) => ({ fecha: j.fecha, clave: `${p.id}-${j.id || j.fecha}` }))
      : (p.fecha_evento ? [{ fecha: p.fecha_evento, clave: `${p.id}-cabecera` }] : []);
    fechas.forEach(({ fecha, clave }) => {
      const dia = String(fecha || '').slice(0, 10);
      if (dia && dia >= inicio && dia <= fin) {
        encontrados.push({ clave, fecha: dia, cliente: p.cliente_nombre || 'Cliente', tipo: p.tipo_evento || 'Evento' });
      }
    });
  });
  return encontrados;
};

// Saldo de vacaciones con las claves nuevas del servidor y, si aún no llegan, las antiguas
const saldoDe = (vac, pendientesLocales) => ({
  anuales: vac?.anuales ?? vac?.dias_vacaciones_anuales ?? null,
  devengados: vac?.devengados ?? vac?.dias_correspondientes_año_actual ?? 0,
  devengadosFinAno: vac?.devengados_fin_ano ?? null,
  usados: vac?.usados ?? vac?.dias_usados ?? 0,
  pendientes: vac?.pendientes_aprobacion ?? pendientesLocales ?? 0,
  disponibles: vac?.disponibles ?? vac?.dias_disponibles ?? 0,
});

// Color map for Spanish color names
const colorMap = {
  verde: '#22c55e', azul: '#3b82f6', naranja: '#f97316', morado: '#a855f7',
  amarillo: '#eab308', rosa: '#f472b6', rojo: '#ef4444', cyan: '#06b6d4',
};

const Av = ({ name, color, size = 36, pulse }) => (
  <div className="relative shrink-0">
    <div className="rounded-full flex items-center justify-center text-white font-bold"
      style={{ backgroundColor: colorMap[color] || color || '#64748b', width: size, height: size, fontSize: size * 0.38 }}>
      {name?.charAt(0)?.toUpperCase() || '?'}
    </div>
    {pulse && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />}
  </div>
);

// Stat card
const Stat = ({ icon: Icon, value, label, color = 'slate', accent }) => {
  const bg = { slate: 'bg-slate-50 text-slate-600', emerald: 'bg-emerald-50 text-emerald-600', amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600', sky: 'bg-sky-50 text-sky-600' };
  return (
    <div data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`} className="bg-white rounded-lg border border-slate-200 p-3.5">
      <div className="flex items-center gap-2.5">
        <div className={`p-1.5 rounded-md ${bg[color]}`}><Icon className="w-4 h-4" /></div>
        <div>
          <p className={`text-lg font-semibold tabular-nums leading-tight ${accent || 'text-slate-900'}`}>{value}</p>
          <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
};

// AI collapsible
const AiPanel = ({ title, icon: Icon, color, items }) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  const c = {
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', sub: 'text-rose-700', icon: 'text-rose-600', dot: 'bg-rose-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', sub: 'text-amber-700', icon: 'text-amber-600', dot: 'bg-amber-500' },
    sky: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-800', sub: 'text-sky-700', icon: 'text-sky-600', dot: 'bg-sky-500' },
  }[color] || { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-800', sub: 'text-sky-700', icon: 'text-sky-600', dot: 'bg-sky-500' };
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button data-testid={`ai-panel-${color}`} className={`w-full flex items-center justify-between ${c.bg} border ${c.border} rounded-lg px-4 py-2.5 text-left hover:opacity-90 transition-colors`}>
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${c.icon}`} />
            <span className={`text-sm font-medium ${c.text}`}>{title}</span>
            <Badge variant="secondary" className="text-[10px] h-5">{items.length}</Badge>
          </div>
          <ChevronDown className={`w-4 h-4 ${c.icon} transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className={`${c.bg} border-x border-b ${c.border} rounded-b-lg px-4 py-3 -mt-1 space-y-1.5`}>
          {items.map((item, i) => (
            <p key={i} className={`text-xs ${c.sub} flex items-start gap-1.5`}>
              <span className={`w-1.5 h-1.5 rounded-full ${c.dot} mt-1.5 shrink-0`} />{item}
            </p>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Aviso de eventos confirmados en las fechas de una ausencia
const AvisoEventos = ({ eventos, fuerte }) => {
  if (!eventos?.length) return null;
  return (
    <div className={`mt-2 p-2.5 rounded-lg border text-xs ${fuerte ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
      <p className="font-medium flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        {eventos.length === 1 ? 'Hay 1 evento confirmado en estas fechas' : `Hay ${eventos.length} eventos confirmados en estas fechas`}
      </p>
      <ul className="mt-1 space-y-0.5">
        {eventos.map((ev) => (
          <li key={ev.clave} className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            <span className="font-medium">{fmtDia(ev.fecha)}</span>
            <span>{ev.tipo} para {ev.cliente}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AUSENCIA_VACIA = {
  empleado_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '', hora: '', lugar: '',
  motivo_ocupado: 'medico', motivo_texto: '',
};

export default function GestionEquipo() {
  const { user } = useAuth();
  const esDireccion = user?.rol === 'direccion' || user?.rol === 'admin';
  // Aprobar, rechazar, crear ausencias y fichajes manuales: dirección y RRHH (lo exige el servidor)
  const puedeGestionar = ['direccion', 'rrhh'].includes(user?.rol);

  const [tab, setTab] = useState('hoy');
  const [empleados, setEmpleados] = useState([]);
  // Lista completa (incluidas las bajas) para resolver nombres en vacaciones
  const [todosEmpleados, setTodosEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fichajesHoy, setFichajesHoy] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal();

  // AI
  const [aiRecs, setAiRecs] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Vacaciones
  const [solicitudes, setSolicitudes] = useState([]);
  const [vacMap, setVacMap] = useState({});
  const [presupuestos, setPresupuestos] = useState([]);
  const [presupuestosCargados, setPresupuestosCargados] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');
  // Diálogos: { sol, comentario } / { sol, inicio, fin } / sol / ausencia nueva
  const [rechazo, setRechazo] = useState(null);
  const [aPendiente, setAPendiente] = useState(null);
  const [cambioFechas, setCambioFechas] = useState(null);
  const [solABorrar, setSolABorrar] = useState(null);
  const [nuevaAusencia, setNuevaAusencia] = useState(null);

  // Cierres
  const [periodosCierre, setPeriodosCierre] = useState([]);
  const [nuevoPeriodo, setNuevoPeriodo] = useState({ nombre: '', fecha_inicio: '', fecha_fin: '', tipo: 'cierre_total' });
  const [cierreABorrar, setCierreABorrar] = useState(null);

  // Registro horario de un empleado (calendario y fichajes manuales)
  const [detailEmp, setDetailEmp] = useState(null);

  // Saldo de vacaciones por empleado
  const cargarSaldos = useCallback(async (lista) => {
    const resultados = await Promise.all(
      lista.map(async (emp) => {
        try { return { id: emp.id, data: await pedir(`/empleados/${emp.id}/vacaciones-disponibles`) }; }
        catch { return { id: emp.id, data: null }; }
      })
    );
    const mapa = {};
    resultados.forEach((r) => { if (r.data) mapa[r.id] = r.data; });
    setVacMap(mapa);
    return mapa;
  }, []);

  const cargarSolicitudes = useCallback(async () => {
    try {
      setSolicitudes(await pedir('/solicitudes-vacaciones') || []);
    } catch {
      setSolicitudes([]);
    }
  }, []);

  const loadAI = useCallback(async (emps, vM, empStats) => {
    setAiLoading(true);
    try {
      const res = await pedir('/gestion-equipo/recomendaciones-ia', {
        method: 'post',
        data: {
          empleados: emps.map(e => ({
            id: e.id, nombre: e.nombre, horas_contrato: e.horas_contrato_semana,
            dias_vacaciones_disponibles: saldoDe(vM[e.id]).disponibles,
            horas_semana_actual: empStats[e.id]?.horasSemana || 0
          })),
          mes_actual: new Date().getMonth() + 1, anio_actual: new Date().getFullYear()
        }
      });
      setAiRecs(res);
    } catch (e) { console.error('AI error:', e); }
    finally { setAiLoading(false); }
  }, []);

  // ---- Load data ----
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rolesExcluidos = ['direccion', 'admin', 'administrador'];
      const [empRes, todosRes, fichajesRes] = await Promise.all([
        pedir('/empleados', { params: { activo: true } }),
        empleadosAPI.getAll().then((r) => r.data).catch(() => []),
        pedir('/fichaje/hoy').catch(() => ({ registros: [] })),
      ]);

      const empleadosData = (empRes || [])
        .filter(e => !rolesExcluidos.includes(e.rol?.toLowerCase()))
        .map(e => ({
          id: e.id, nombre: e.nombre, nombre_completo: e.nombre_completo,
          rol: e.rol, color: e.color, telefono: e.telefono, email: e.email,
          horas_contrato_semana: e.horas_contrato_semana || 40,
          dias_vacaciones_anuales: e.dias_vacaciones_anuales || 30, fecha_alta: e.fecha_alta,
        }));
      setEmpleados(empleadosData);
      setTodosEmpleados(todosRes || []);
      setFichajesHoy(fichajesRes?.registros || []);

      const [cierreRes] = await Promise.all([
        pedir('/periodos-cierre').catch(() => []),
        cargarSolicitudes(),
      ]);
      setPeriodosCierre(cierreRes || []);

      const vM = await cargarSaldos(empleadosData);

      // Fichajes del mes de cada empleado (la semana se filtra en el cliente)
      const empStats = {};
      await Promise.all(
        empleadosData.map(async (emp) => {
          try {
            const r = await pedir(`/fichaje/empleado/${emp.id}`);
            empStats[emp.id] = calcularStats(emp, r?.registros || []);
          } catch {
            empStats[emp.id] = statsVacias(emp);
          }
        })
      );
      setStats(empStats);

      loadAI(empleadosData, vM, empStats);
    } catch (err) { console.error('Error loading:', err); }
    finally { setLoading(false); }
  }, [cargarSaldos, cargarSolicitudes, loadAI]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto refresh fichajes every 60s
  useEffect(() => {
    const iv = setInterval(async () => {
      try { const r = await pedir('/fichaje/hoy'); setFichajesHoy(r?.registros || []); } catch {}
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  // Eventos confirmados: solo al abrir la pestaña Vacaciones (para el aviso de fechas)
  useEffect(() => {
    if (tab !== 'vacaciones' || presupuestosCargados) return;
    setPresupuestosCargados(true);
    api.get('/presupuestos', { params: { estado: 'confirmado', limit: 200 } })
      .then((res) => setPresupuestos(Array.isArray(res.data) ? res.data : (res.data?.presupuestos || [])))
      .catch((error) => console.error('Error cargando eventos confirmados:', error));
  }, [tab, presupuestosCargados]);

  // Tras cambiar un fichaje en el registro horario, recalcula las horas de ese empleado
  const recargarStatsEmpleado = useCallback(async (emp) => {
    if (!emp) return;
    try {
      const r = await pedir(`/fichaje/empleado/${emp.id}`);
      setStats((prev) => ({ ...prev, [emp.id]: calcularStats(emp, r?.registros || []) }));
    } catch (e) { console.error(e); }
  }, []);

  const recargarVacaciones = useCallback(async () => {
    await Promise.all([cargarSolicitudes(), cargarSaldos(empleados)]);
  }, [cargarSolicitudes, cargarSaldos, empleados]);

  // Derived
  const trabajando = fichajesHoy.filter(f => f.hora_entrada && !f.hora_salida);
  const filtered = empleados.filter(e => !searchTerm || (e.nombre_completo || e.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()));
  const totalHSemana = Object.values(stats).reduce((s, st) => s + (st.horasSemana || 0), 0);
  const totalHContrato = empleados.reduce((s, e) => s + (e.horas_contrato_semana || 40), 0);
  const totalVacPend = Object.values(vacMap).reduce((s, v) => s + (saldoDe(v).disponibles || 0), 0);
  const alertasCount = Object.values(stats).filter(st => st.exceso).length;

  const pendientes = solicitudes.filter((s) => s.estado === 'pendiente');
  const procesadas = solicitudes.filter((s) => s.estado !== 'pendiente');
  const anioActual = String(new Date().getFullYear());

  // Días de vacaciones pendientes de aprobar por empleado (si el servidor aún no los da)
  const pendientesPorEmpleado = useMemo(() => {
    const mapa = {};
    solicitudes
      .filter((s) => s.estado === 'pendiente' && s.tipo === 'vacaciones' && String(s.fecha_inicio || '').startsWith(anioActual))
      .forEach((s) => { mapa[s.empleado_id] = (mapa[s.empleado_id] || 0) + (s.dias_solicitados || 0); });
    return mapa;
  }, [solicitudes, anioActual]);

  const eventosPorSolicitud = useMemo(() => {
    const mapa = {};
    solicitudes.forEach((s) => {
      const eventos = eventosEnRango(presupuestos, s.fecha_inicio, s.fecha_fin);
      if (eventos.length > 0) mapa[s.id] = eventos;
    });
    return mapa;
  }, [solicitudes, presupuestos]);

  const nombreEmpleado = (id) => {
    const emp = todosEmpleados.find((e) => e.id === id) || empleados.find((e) => e.id === id);
    return emp ? (emp.nombre || emp.nombre_completo) : 'Empleado sin ficha';
  };

  // Actions
  const refreshFichajes = async () => {
    try { const r = await pedir('/fichaje/hoy'); setFichajesHoy(r?.registros || []); } catch {}
  };

  const cerrarDialogos = () => {
    setRechazo(null);
    setAPendiente(null);
    setCambioFechas(null);
    setSolABorrar(null);
    setNuevaAusencia(null);
    setCierreABorrar(null);
    setErrorDialogo('');
  };

  // Ejecuta una acción de un diálogo: error dentro del diálogo, éxito como aviso
  const ejecutar = async (accion, textoOk, porDefecto) => {
    setProcesando(true);
    setErrorDialogo('');
    try {
      await accion();
      cerrarDialogos();
      mostrarAviso('ok', textoOk);
      return true;
    } catch (error) {
      console.error(error);
      setErrorDialogo(textoErrorApi(error.response?.data?.detail, porDefecto));
      return false;
    } finally {
      setProcesando(false);
    }
  };

  const aprobar = async (sol) => {
    limpiarAviso();
    setProcesando(true);
    try {
      await pedir(`/solicitudes-vacaciones/${sol.id}/aprobar`, { method: 'patch' });
      await recargarVacaciones();
      mostrarAviso('ok', `Aprobada la ausencia de ${nombreEmpleado(sol.empleado_id)}`);
    } catch (error) {
      console.error(error);
      mostrarAviso('error', textoErrorApi(error.response?.data?.detail, 'No se ha podido aprobar'));
    } finally {
      setProcesando(false);
    }
  };

  const confirmarRechazo = () => ejecutar(async () => {
    const comentario = (rechazo.comentario || '').trim();
    await pedir(`/solicitudes-vacaciones/${rechazo.sol.id}/rechazar`, {
      method: 'patch',
      params: comentario ? { comentarios: comentario } : undefined,
    });
    await recargarVacaciones();
  }, 'Solicitud rechazada', 'No se ha podido rechazar');

  const confirmarAPendiente = () => ejecutar(async () => {
    const comentario = (aPendiente.comentario || '').trim();
    await pedir(`/solicitudes-vacaciones/${aPendiente.sol.id}/cambiar-estado`, {
      method: 'patch',
      params: comentario ? { nuevo_estado: 'pendiente', comentarios: comentario } : { nuevo_estado: 'pendiente' },
    });
    await recargarVacaciones();
  }, 'La solicitud vuelve a estar pendiente', 'No se ha podido cambiar el estado');

  const confirmarFechas = () => {
    const { sol, inicio, fin } = cambioFechas;
    if (!inicio || !fin) {
      setErrorDialogo('Indica las dos fechas');
      return;
    }
    if (fin < inicio) {
      setErrorDialogo('La fecha de fin no puede ser anterior a la de inicio');
      return;
    }
    ejecutar(async () => {
      await pedir(`/solicitudes-vacaciones/${sol.id}`, {
        method: 'put',
        data: { fecha_inicio: inicio, fecha_fin: fin, dias_solicitados: diasEntre(inicio, fin) },
      });
      await recargarVacaciones();
    }, 'Fechas modificadas', 'No se han podido modificar las fechas');
  };

  const confirmarBorrarSolicitud = () => ejecutar(async () => {
    await pedir(`/solicitudes-vacaciones/${solABorrar.id}`, { method: 'delete' });
    await recargarVacaciones();
  }, 'Solicitud eliminada', 'No se ha podido eliminar');

  // Ausencia registrada por dirección o RRHH: se crea y se aprueba
  const confirmarNuevaAusencia = async () => {
    const {
      empleado_id, tipo, fecha_inicio, fecha_fin, motivo, hora, lugar,
      motivo_ocupado, motivo_texto,
    } = nuevaAusencia;
    const ocupado = tipo === 'dia_ocupado';
    if (!empleado_id || !fecha_inicio || !fecha_fin) {
      setErrorDialogo('Selecciona empleado y fechas de inicio y fin');
      return;
    }
    if (ocupado && motivo_ocupado === 'otro' && !String(motivo_texto || '').trim()) {
      setErrorDialogo('Con el motivo Otro, escribe cuál es');
      return;
    }
    if (fecha_fin < fecha_inicio) {
      setErrorDialogo('La fecha de fin no puede ser anterior a la de inicio');
      return;
    }
    setProcesando(true);
    setErrorDialogo('');
    try {
      const creada = await pedir('/solicitudes-vacaciones', {
        method: 'post',
        data: {
          empleado_id, tipo, fecha_inicio, fecha_fin,
          dias_solicitados: diasEntre(fecha_inicio, fecha_fin),
          motivo: motivo || 'Registrada por dirección',
          ...(ocupado ? {
            hora: hora || null,
            lugar: lugar || null,
            motivo_ocupado,
            motivo_texto: String(motivo_texto || '').trim() || null,
          } : {}),
        },
      });
      // El día ocupado ya se guarda aprobado (y sin aviso a la persona)
      let aprobada = true;
      if (creada?.estado !== 'aprobada') {
        try {
          await pedir(`/solicitudes-vacaciones/${creada.id}/aprobar`, { method: 'patch' });
        } catch (errorAprobar) {
          console.error(errorAprobar);
          aprobada = false;
        }
      }
      cerrarDialogos();
      await recargarVacaciones();
      if (aprobada) mostrarAviso('ok', 'Ausencia registrada y aprobada');
      else mostrarAviso('error', 'La ausencia se ha creado pero ha quedado pendiente: no se ha podido aprobar');
    } catch (error) {
      console.error(error);
      setErrorDialogo(textoErrorApi(error.response?.data?.detail, 'Error al crear la ausencia'));
    } finally {
      setProcesando(false);
    }
  };

  const handleAddCierre = async () => {
    if (!nuevoPeriodo.nombre || !nuevoPeriodo.fecha_inicio || !nuevoPeriodo.fecha_fin) {
      mostrarAviso('error', 'Indica nombre, inicio y fin del periodo de cierre');
      return;
    }
    try {
      const r = await pedir('/periodos-cierre', { method: 'post', data: nuevoPeriodo });
      setPeriodosCierre(prev => [...prev, r]);
      setNuevoPeriodo({ nombre: '', fecha_inicio: '', fecha_fin: '', tipo: 'cierre_total' });
      mostrarAviso('ok', 'Periodo de cierre añadido');
    } catch (e) {
      console.error(e);
      mostrarAviso('error', textoErrorApi(e.response?.data?.detail, 'No se ha podido añadir el periodo'));
    }
  };

  const confirmarBorrarCierre = () => ejecutar(async () => {
    await pedir(`/periodos-cierre/${cierreABorrar.id}`, { method: 'delete' });
    setPeriodosCierre(prev => prev.filter(p => p.id !== cierreABorrar.id));
  }, 'Periodo de cierre eliminado', 'No se ha podido eliminar el periodo');

  const abrirDialogo = (abrir) => {
    limpiarAviso();
    setErrorDialogo('');
    abrir();
  };

  // Botones de cada solicitud según su estado
  const accionesSolicitud = (sol) => {
    if (!puedeGestionar) return null;
    const btn = 'px-2.5 py-1 text-xs rounded-md font-medium disabled:opacity-50';
    return (
      <div className="flex flex-wrap gap-1.5 justify-end">
        {sol.estado === 'pendiente' && (
          <>
            <button data-testid={`approve-vac-${sol.id}`} disabled={procesando} onClick={() => aprobar(sol)} className={`${btn} bg-emerald-100 text-emerald-800 hover:bg-emerald-200 inline-flex items-center gap-1`}>
              <Check className="w-3.5 h-3.5" /> Aprobar
            </button>
            <button data-testid={`reject-vac-${sol.id}`} onClick={() => abrirDialogo(() => setRechazo({ sol, comentario: '' }))} className={`${btn} bg-rose-100 text-rose-800 hover:bg-rose-200 inline-flex items-center gap-1`}>
              <X className="w-3.5 h-3.5" /> Rechazar
            </button>
          </>
        )}
        {sol.estado === 'aprobada' && (
          <button data-testid={`reject-vac-${sol.id}`} onClick={() => abrirDialogo(() => setRechazo({ sol, comentario: '' }))} className={`${btn} bg-rose-100 text-rose-800 hover:bg-rose-200`}>
            Rechazar
          </button>
        )}
        {sol.estado === 'rechazada' && (
          <button data-testid={`approve-vac-${sol.id}`} disabled={procesando} onClick={() => aprobar(sol)} className={`${btn} bg-emerald-100 text-emerald-800 hover:bg-emerald-200`}>
            Aprobar
          </button>
        )}
        {sol.estado !== 'pendiente' && (
          <button data-testid={`pendiente-vac-${sol.id}`} onClick={() => abrirDialogo(() => setAPendiente({ sol, comentario: '' }))} className={`${btn} bg-amber-100 text-amber-800 hover:bg-amber-200`}>
            A pendiente
          </button>
        )}
        <button data-testid={`fechas-vac-${sol.id}`} onClick={() => abrirDialogo(() => setCambioFechas({ sol, inicio: String(sol.fecha_inicio || '').slice(0, 10), fin: String(sol.fecha_fin || '').slice(0, 10) }))} className={`${btn} border border-slate-300 text-slate-700 hover:bg-slate-100`}>
          Fechas
        </button>
        <button data-testid={`delete-vac-${sol.id}`} onClick={() => abrirDialogo(() => setSolABorrar(sol))} className={`${btn} bg-red-600 text-white hover:bg-red-700`}>
          Eliminar
        </button>
      </div>
    );
  };

  // Datos de una solicitud (nombre, tipo, fechas, días, motivo y eventos)
  const filaSolicitud = (sol, conEstado) => (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-slate-800">{nombreEmpleado(sol.empleado_id)}</p>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${esDiaOcupado(sol) ? 'bg-amber-100 text-amber-900' : 'bg-sky-100 text-sky-800'}`}>{textoTipo(sol)}</span>
        {conEstado && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
            sol.estado === 'aprobada' ? 'bg-emerald-100 text-emerald-800' : sol.estado === 'rechazada' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'
          }`}>
            {sol.estado === 'aprobada' ? 'Aprobada' : sol.estado === 'rechazada' ? 'Rechazada' : sol.estado}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">
        {fmtDia(sol.fecha_inicio)} a {fmtDia(sol.fecha_fin)} · {sol.dias_solicitados ?? diasEntre(sol.fecha_inicio, sol.fecha_fin)} días
      </p>
      {esDiaOcupado(sol) && (sol.hora || sol.lugar) && (
        <p className="text-xs text-slate-500 mt-0.5">{[sol.hora, sol.lugar].filter(Boolean).join(' · ')}</p>
      )}
      {sol.motivo && <p className="text-xs text-slate-500 italic mt-0.5">&quot;{sol.motivo}&quot;</p>}
      {sol.comentarios_admin && <p className="text-xs text-slate-500 mt-0.5">Comentario: {sol.comentarios_admin}</p>}
      <AvisoEventos eventos={eventosPorSolicitud[sol.id]} fuerte={sol.estado === 'pendiente'} />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3" data-testid="loading-spinner">
        <Loader2 className="w-5 h-5 animate-spin text-teal-700" />
        <span className="text-slate-500 text-sm">Cargando equipo...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="gestion-equipo-page">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900 tracking-tight" data-testid="page-title">Gestión de Equipo</h1>
        <p className="text-sm text-slate-500 mt-0.5">Control de jornadas, vacaciones y personal</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-5">
        <Stat icon={Users} value={empleados.length} label="Empleados activos" />
        <Stat icon={Activity} value={trabajando.length} label="Trabajando ahora" color="emerald" accent="text-emerald-700" />
        <Stat icon={Timer} value={`${totalHSemana.toFixed(1)}h / ${totalHContrato}h`} label="Horas semana" color="sky" />
        <Stat icon={Sun} value={`${totalVacPend} días`} label="Vacaciones pendientes" color="amber" />
        <Stat icon={AlertTriangle} value={alertasCount} label="Alertas normativa" color={alertasCount > 0 ? 'rose' : 'slate'} accent={alertasCount > 0 ? 'text-rose-600' : undefined} />
      </div>

      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-3" testId="gestion-equipo-aviso" />

      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList className="bg-slate-100 p-1 h-auto gap-0.5" data-testid="main-tabs">
          <TabsTrigger value="hoy" data-testid="tab-hoy" className="text-xs sm:text-sm gap-1.5 data-[state=active]:bg-white">
            <Activity className="w-3.5 h-3.5" /> Hoy
          </TabsTrigger>
          <TabsTrigger value="equipo" data-testid="tab-equipo" className="text-xs sm:text-sm gap-1.5 data-[state=active]:bg-white">
            <Users className="w-3.5 h-3.5" /> Equipo y Horas
          </TabsTrigger>
          <TabsTrigger value="vacaciones" data-testid="tab-vacaciones" className="text-xs sm:text-sm gap-1.5 data-[state=active]:bg-white">
            <Sun className="w-3.5 h-3.5" /> Vacaciones
            {pendientes.length > 0 && <Badge variant="destructive" className="text-[10px] h-5 ml-1">{pendientes.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="normativa" data-testid="tab-normativa" className="text-xs sm:text-sm gap-1.5 data-[state=active]:bg-white">
            <Info className="w-3.5 h-3.5" /> Normativa
          </TabsTrigger>
        </TabsList>

        {/* ==================== HOY ==================== */}
        <TabsContent value="hoy">
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-lg" data-testid="working-now-section">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-slate-800">Trabajando ahora</span>
                  <Badge variant="secondary" className="text-[10px] h-5">{trabajando.length}</Badge>
                </div>
                <button data-testid="refresh-fichajes-btn" onClick={refreshFichajes} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Actualizar
                </button>
              </div>
              <div className="p-4">
                {trabajando.length === 0 ? (
                  <p className="text-sm text-slate-400">Nadie fichado en este momento</p>
                ) : (
                  <div className="flex flex-wrap gap-2.5">
                    {trabajando.map((f, i) => (
                      <div key={i} data-testid={`working-${i}`} className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
                        <Av name={f.empleado_nombre} color={f.empleado_color} size={32} pulse />
                        <div>
                          <p className="text-sm font-medium text-slate-800 leading-tight">{f.empleado_nombre}</p>
                          <p className="text-[11px] text-emerald-700">Desde {fmtTime(f.hora_entrada)} -- {elapsed(f.hora_entrada)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg" data-testid="fichajes-today">
              <div className="px-4 py-2.5 border-b border-slate-100">
                <span className="text-sm font-medium text-slate-800">Fichajes del dia</span>
              </div>
              {fichajesHoy.length === 0 ? (
                <p className="px-4 py-5 text-sm text-slate-400 text-center">Sin fichajes hoy</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {fichajesHoy.map((f, i) => {
                    const activo = f.hora_entrada && !f.hora_salida;
                    return (
                      <div key={i} className="px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Av name={f.empleado_nombre} color={f.empleado_color} size={28} pulse={activo} />
                          <div>
                            <p className="text-sm font-medium text-slate-700 leading-tight">{f.empleado_nombre}</p>
                            <p className="text-[11px] text-slate-400">{fmtTime(f.hora_entrada)}{f.hora_salida ? ` - ${fmtTime(f.hora_salida)}` : ''}</p>
                          </div>
                        </div>
                        {f.hora_salida ? (
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{Math.floor((f.minutos_trabajados || 0) / 60)}h {(f.minutos_trabajados || 0) % 60}m</span>
                        ) : (
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Activo
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2" data-testid="ai-recommendations">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Recomendaciones IA</span>
                {aiLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
              <AiPanel title="Alertas" icon={AlertTriangle} color="rose" items={aiRecs?.alertas} />
              <AiPanel title="Vacaciones" icon={Sun} color="amber" items={aiRecs?.vacaciones} />
              <AiPanel title="Descansos y jornadas" icon={Clock} color="sky" items={aiRecs?.descansos} />
            </div>
          </div>
        </TabsContent>

        {/* ==================== EQUIPO Y HORAS ==================== */}
        <TabsContent value="equipo">
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="search-employees" type="text" placeholder="Buscar empleado..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="employee-list">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 font-medium text-slate-500 uppercase text-xs">Empleado</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs">Horas semana</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs hidden sm:table-cell">Días semana</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs hidden md:table-cell">Sem.</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs hidden lg:table-cell">Mes</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs hidden lg:table-cell">Días mes</th>
                      <th className="text-center px-3 py-2.5 font-medium text-slate-500 uppercase text-xs">Estado</th>
                      <th className="text-right px-3 py-2.5 font-medium text-slate-500 uppercase text-xs w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(emp => {
                      const st = stats[emp.id] || {};
                      const activo = trabajando.some(f => f.empleado_id === emp.id);
                      return (
                        <tr key={emp.id} data-testid={`employee-row-${emp.id}`} className={`hover:bg-slate-50/50 transition-colors ${detailEmp?.id === emp.id ? 'bg-teal-50/40' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <Av name={emp.nombre} color={emp.color} size={36} pulse={activo} />
                              <div>
                                <p className="font-medium text-slate-900 leading-tight">{emp.nombre}</p>
                                <p className="text-[11px] text-slate-400">{emp.horas_contrato_semana}h/semana</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-bold text-slate-900">{st.horasSemana || 0}h</span>
                          </td>
                          <td className="px-3 py-3 text-center hidden sm:table-cell">
                            <span className="font-medium text-slate-700">{st.diasSemana || 0} días</span>
                          </td>
                          <td className="px-3 py-3 hidden md:table-cell">
                            {/* Weekly progress bar */}
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all rounded-full ${
                                    st.pctSemana >= 100 ? 'bg-emerald-500' : st.pctSemana >= 80 ? 'bg-sky-500' : 'bg-amber-500'
                                  }`}
                                  style={{ width: `${st.pctSemana || 0}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-500 w-8 text-right">{(st.pctSemana || 0).toFixed(0)}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 hidden lg:table-cell" data-testid={`month-progress-${emp.id}`}>
                            {/* Monthly progress bar with expected marker */}
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 relative h-3 bg-slate-200 rounded-full overflow-visible">
                                  <div
                                    className={`absolute inset-y-0 left-0 transition-all rounded-full ${
                                      (st.pctMes || 0) > (st.pctMesEsperado || 0) ? 'bg-emerald-500' : 'bg-amber-500'
                                    }`}
                                    style={{ width: `${Math.min(st.pctMes || 0, 100)}%` }}
                                  />
                                  {(st.pctMesEsperado || 0) > 0 && (
                                    <div
                                      className="absolute top-[-2px] bottom-[-2px] w-[3px] bg-red-500 rounded-full z-10"
                                      style={{ left: `${Math.min(st.pctMesEsperado, 100)}%`, transform: 'translateX(-50%)' }}
                                      title={`Esperado: ${st.pctMesEsperado?.toFixed(0)}% (${((st.contratoMes || 0) * (st.pctMesEsperado || 0) / 100).toFixed(0)}h de ${st.contratoMes || 0}h)`}
                                    />
                                  )}
                                </div>
                                <span className="text-xs text-slate-500 w-8 text-right">{(st.pctMes || 0).toFixed(0)}%</span>
                              </div>
                              <div className="flex justify-between text-[10px] text-slate-400 px-0.5">
                                <span className="font-medium text-slate-600">{st.horasMes || 0}h</span>
                                <span>/ {st.contratoMes || 0}h</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center hidden lg:table-cell">
                            <span className="font-medium text-slate-700">{st.diasMes || 0} días</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {st.exceso ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                                <AlertTriangle className="w-3 h-3" /> Exceso
                              </span>
                            ) : activo ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> Activo
                              </span>
                            ) : st.horasSemana > 0 ? (
                              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">OK</span>
                            ) : (
                              <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sin fichajes</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <button data-testid={`detail-btn-${emp.id}`} onClick={() => setDetailEmp(detailEmp?.id === emp.id ? null : emp)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Registro horario">
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <Link data-testid={`ficha-link-${emp.id}`} to={`/empleados/${emp.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Ver ficha">
                                <User className="w-3.5 h-3.5" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Registro horario del empleado: calendario mensual y fichajes manuales */}
            {detailEmp && (
              <div className="bg-white border border-slate-200 rounded-lg" data-testid="employee-detail">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Av name={detailEmp.nombre} color={detailEmp.color} size={40} />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{detailEmp.nombre_completo || detailEmp.nombre}</p>
                      <p className="text-xs text-slate-500 capitalize truncate">{[detailEmp.rol, detailEmp.email].filter(Boolean).join(' · ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Link data-testid="detail-ficha-link" to={`/empleados/${detailEmp.id}`} className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /> Ver ficha
                    </Link>
                    <button data-testid="close-detail-btn" onClick={() => setDetailEmp(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Cerrar">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <RegistroHorarioEmpleado
                    key={detailEmp.id}
                    empleadoId={detailEmp.id}
                    onCambio={() => recargarStatsEmpleado(detailEmp)}
                  />
                </div>
              </div>
            )}

            <AiPanel title="Recomendaciones de descansos (IA)" icon={Clock} color="sky" items={aiRecs?.descansos} />
          </div>
        </TabsContent>

        {/* ==================== VACACIONES ==================== */}
        <TabsContent value="vacaciones">
          <div className="space-y-3">
            <AiPanel title="Recomendaciones de vacaciones (IA)" icon={Sun} color="amber" items={aiRecs?.vacaciones} />

            {puedeGestionar ? (
              <div className="flex justify-end">
                <button
                  data-testid="add-ausencia-btn"
                  onClick={() => {
                    abrirDialogo(() => setNuevaAusencia({ ...AUSENCIA_VACIA }));
                    revisarNovedades();
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-700 text-white text-sm font-medium rounded hover:bg-teal-800"
                >
                  <Plus className="w-4 h-4" /> Añadir ausencia
                </button>
              </div>
            ) : (
              <div className="px-4 py-2.5 border border-slate-200 rounded-lg bg-slate-50/50 flex items-center gap-2 text-slate-500 text-xs">
                <Lock className="w-3.5 h-3.5" /> Solo dirección y RRHH pueden aprobar, rechazar o registrar ausencias
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg" data-testid="vacation-requests">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">Solicitudes pendientes</span>
                {pendientes.length > 0 && <Badge variant="destructive" className="text-[10px] h-5">{pendientes.length}</Badge>}
              </div>
              {pendientes.length === 0 ? (
                <p className="px-4 py-5 text-sm text-slate-400 text-center">Sin solicitudes pendientes</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {pendientes.map(sol => (
                    <div key={sol.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-testid={`vac-request-${sol.id}`}>
                      {filaSolicitud(sol, false)}
                      {accionesSolicitud(sol)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg" data-testid="vacation-balance">
              <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">Saldo de vacaciones {anioActual}</span>
                <span className="text-[11px] text-slate-400">Disponibles = generados hasta hoy menos usados (las altas del año devengan día a día); las pendientes de aprobar se muestran aparte</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 divide-slate-100">
                {empleados.map(emp => {
                  const saldo = saldoDe(vacMap[emp.id], pendientesPorEmpleado[emp.id] || 0);
                  const pct = saldo.devengados ? (saldo.usados / saldo.devengados * 100) : 0;
                  return (
                    <div key={emp.id} className="p-4 border-b sm:border-b-0 sm:border-r border-slate-100 last:border-0" data-testid={`vac-balance-${emp.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Av name={emp.nombre} color={emp.color} size={28} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{emp.nombre}</p>
                          <p className="text-[11px] text-slate-400">{saldo.anuales ?? emp.dias_vacaciones_anuales} días/año · generados hasta hoy {saldo.devengados}{saldo.devengadosFinAno != null && saldo.devengadosFinAno !== saldo.devengados ? ` · al cierre del año ${saldo.devengadosFinAno}` : ''}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-xs mb-1.5">
                        <span className="text-slate-500">Disponibles <strong className="text-amber-600 block text-sm">{saldo.disponibles}</strong></span>
                        <span className="text-slate-500">Usados <strong className="text-slate-700 block text-sm">{saldo.usados}</strong></span>
                        <span className="text-slate-500">Pendientes <strong className="text-sky-700 block text-sm">{saldo.pendientes}</strong></span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg" data-testid="vacation-history">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-800">Historial de solicitudes</span>
                <Badge variant="secondary" className="text-[10px] h-5">{procesadas.length}</Badge>
              </div>
              {procesadas.length === 0 ? (
                <p className="px-4 py-5 text-sm text-slate-400 text-center">Sin solicitudes procesadas</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {procesadas.map(sol => (
                    <div key={sol.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-testid={`vac-history-${sol.id}`}>
                      {filaSolicitud(sol, true)}
                      {accionesSolicitud(sol)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg" data-testid="cierres-section">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <CalendarX className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-800">Periodos de cierre</span>
              </div>
              {esDireccion && (
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <input data-testid="cierre-nombre" type="text" placeholder="Nombre" value={nuevoPeriodo.nombre} onChange={e => setNuevoPeriodo(p => ({ ...p, nombre: e.target.value }))}
                      className="px-2.5 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-teal-500" />
                    <input data-testid="cierre-inicio" type="date" value={nuevoPeriodo.fecha_inicio} onChange={e => setNuevoPeriodo(p => ({ ...p, fecha_inicio: e.target.value }))}
                      className="px-2.5 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-teal-500" />
                    <input data-testid="cierre-fin" type="date" value={nuevoPeriodo.fecha_fin} onChange={e => setNuevoPeriodo(p => ({ ...p, fecha_fin: e.target.value }))}
                      className="px-2.5 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-teal-500" />
                    <select data-testid="cierre-tipo" value={nuevoPeriodo.tipo} onChange={e => setNuevoPeriodo(p => ({ ...p, tipo: e.target.value }))}
                      className="px-2.5 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-teal-500">
                      <option value="cierre_total">Cierre total</option>
                      <option value="servicio_minimo">Servicio mínimo</option>
                    </select>
                    <button data-testid="add-cierre-btn" onClick={handleAddCierre} className="px-3 py-1.5 bg-teal-700 text-white text-sm font-medium rounded hover:bg-teal-800">
                      <Plus className="w-4 h-4 inline -mt-0.5 mr-1" />Añadir
                    </button>
                  </div>
                </div>
              )}
              {!esDireccion && (
                <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2 text-slate-400 text-xs">
                  <Lock className="w-3.5 h-3.5" /> Solo dirección puede gestionar periodos de cierre
                </div>
              )}
              {periodosCierre.length === 0 ? (
                <p className="px-4 py-5 text-sm text-slate-400 text-center">Sin periodos de cierre</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {periodosCierre.map(p => (
                    <div key={p.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`cierre-${p.id}`}>
                      <div className="flex items-center gap-2.5">
                        <CalendarX className="w-4 h-4 text-rose-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{p.nombre}</p>
                          <p className="text-xs text-slate-500">{fmtDate(p.fecha_inicio)} - {fmtDate(p.fecha_fin)}</p>
                        </div>
                      </div>
                      {esDireccion && (
                        <button data-testid={`delete-cierre-${p.id}`} onClick={() => abrirDialogo(() => setCierreABorrar(p))} className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded" title="Eliminar periodo"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ==================== NORMATIVA ==================== */}
        <TabsContent value="normativa">
          <div className="space-y-3">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4" data-testid="normativa-header">
              <h3 className="text-sm font-medium text-sky-800 mb-0.5">Normativa aplicable -- Hosteleria Madrid</h3>
              <p className="text-xs text-sky-700">Convenio Colectivo de Hosteleria de la Comunidad de Madrid + Estatuto de los Trabajadores. Personal contratado como camareros.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="normativa-jornada">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-700" />
                  <h4 className="text-sm font-medium text-slate-800">Jornada laboral</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    { t: 'Jornada maxima anual', d: '1.784 horas (Madrid). Equivale a 40h/semana de media anual.' },
                    { t: 'Jornada maxima diaria', d: '9 horas de trabajo efectivo. Solo ampliable a 12h en temporada turistica (RD 1561/1995), NO en catering estandar.' },
                    { t: 'Jornada partida', d: 'Permitida. Descanso minimo de 1,5h entre tramos (convenio Madrid).' },
                    { t: 'Distribucion irregular', d: 'Permitida por estacionalidad, compensando excesos en periodos bajos.' },
                  ].map((item, i) => <li key={i} className="text-xs text-slate-600"><strong className="text-slate-700">{item.t}:</strong> {item.d}</li>)}
                </ul>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="normativa-descansos">
                <div className="flex items-center gap-2 mb-3">
                  <Timer className="w-4 h-4 text-amber-700" />
                  <h4 className="text-sm font-medium text-slate-800">Descansos obligatorios</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    { t: 'Entre jornadas', d: '12 horas consecutivas minimo. En temporada turistica reducible a 10h con compensacion.' },
                    { t: 'Descanso semanal', d: '36 horas consecutivas (dia y medio). En Madrid ampliable a 2 dias.' },
                    { t: 'Pausa intra-jornada', d: '15 minutos si jornada continua supera 6 horas.' },
                    { t: 'Menores de 18', d: '30 minutos si jornada supera 4,5 horas.' },
                  ].map((item, i) => <li key={i} className="text-xs text-slate-600"><strong className="text-slate-700">{item.t}:</strong> {item.d}</li>)}
                </ul>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="normativa-vacaciones">
                <div className="flex items-center gap-2 mb-3">
                  <Sun className="w-4 h-4 text-orange-700" />
                  <h4 className="text-sm font-medium text-slate-800">Vacaciones</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    { t: 'Minimo legal', d: '30 dias naturales por ano. Convenio Madrid puede mejorar.' },
                    { t: 'Proporcional', d: '2,5 dias naturales por mes trabajado.' },
                    { t: 'No compensable', d: 'No sustituible por dinero, salvo extincion de contrato.' },
                    { t: 'Planificacion', d: 'Avisar con 2 meses de antelacion.' },
                  ].map((item, i) => <li key={i} className="text-xs text-slate-600"><strong className="text-slate-700">{item.t}:</strong> {item.d}</li>)}
                </ul>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="normativa-horas-extra">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-rose-700" />
                  <h4 className="text-sm font-medium text-slate-800">Horas extraordinarias</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    { t: 'Maximo anual', d: '80 horas. Las de fuerza mayor no computan.' },
                    { t: 'Voluntarias', d: 'Salvo pacto en convenio.' },
                    { t: 'Compensacion', d: 'Pago con recargo o tiempo de descanso equivalente.' },
                    { t: 'Nocturnidad (22:00-06:00)', d: 'Complemento salarial. Jornada nocturna max 8h de media/15 dias.' },
                  ].map((item, i) => <li key={i} className="text-xs text-slate-600"><strong className="text-slate-700">{item.t}:</strong> {item.d}</li>)}
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="normativa-12h">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                <h4 className="text-sm font-medium text-amber-800">Jornadas de 12 horas en hosteleria</h4>
              </div>
              <div className="text-xs text-amber-800 space-y-1.5">
                <p>Las jornadas de 12 horas <strong>no son legales como norma general</strong> para camareros en Madrid. El maximo ordinario es de 9 horas diarias.</p>
                <p>Solo se permiten en <strong>establecimientos de temporada turistica</strong> (RD 1561/1995), con condiciones:</p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>Media semanal no superar 40h en computo anual</li>
                  <li>Descanso entre jornadas reducible a 10h con compensacion</li>
                  <li>Descanso semanal acumulable hasta 4 meses</li>
                </ul>
                <p>En <strong>catering estandar</strong>, las jornadas de 12h no estan amparadas. Se debe compensar como horas extraordinarias y respetar 12h de descanso entre jornadas.</p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="normativa-reduccion">
              <div className="flex items-center gap-2 mb-1">
                <Info className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-medium text-slate-700">Reduccion de jornada a 37,5h</h4>
              </div>
              <p className="text-xs text-slate-600">Medida planteada pero no en vigor definitivo a febrero 2026. Consultar actualizaciones del convenio de hosteleria de Madrid.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ==================== DIÁLOGOS ==================== */}
      <DialogoConfirmar
        abierto={!!rechazo}
        titulo={rechazo ? `Rechazar la ausencia de ${nombreEmpleado(rechazo.sol.empleado_id)}` : ''}
        descripcion="Se avisa al empleado. El motivo es opcional y le llega en el aviso."
        textoConfirmar="Rechazar"
        peligro
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarRechazo}
        onCancelar={cerrarDialogos}
        testId="dialogo-rechazar-vac"
      >
        <textarea
          value={rechazo?.comentario || ''}
          onChange={(e) => setRechazo((prev) => (prev ? { ...prev, comentario: e.target.value } : prev))}
          rows={2}
          placeholder="Motivo del rechazo (opcional)"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!aPendiente}
        titulo="Volver a pendiente"
        descripcion={aPendiente ? `La solicitud de ${nombreEmpleado(aPendiente.sol.empleado_id)} vuelve a quedar pendiente de aprobar.` : ''}
        textoConfirmar="A pendiente"
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarAPendiente}
        onCancelar={cerrarDialogos}
        testId="dialogo-pendiente-vac"
      >
        <textarea
          value={aPendiente?.comentario || ''}
          onChange={(e) => setAPendiente((prev) => (prev ? { ...prev, comentario: e.target.value } : prev))}
          rows={2}
          placeholder="Comentario (opcional)"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!cambioFechas}
        titulo="Cambiar fechas"
        descripcion={cambioFechas ? `Ausencia de ${nombreEmpleado(cambioFechas.sol.empleado_id)}. Los días se recalculan en naturales.` : ''}
        textoConfirmar="Guardar fechas"
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarFechas}
        onCancelar={cerrarDialogos}
        testId="dialogo-fechas-vac"
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-slate-700">
            Desde
            <input
              type="date"
              value={cambioFechas?.inicio || ''}
              onChange={(e) => setCambioFechas((prev) => (prev ? { ...prev, inicio: e.target.value } : prev))}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Hasta
            <input
              type="date"
              value={cambioFechas?.fin || ''}
              min={cambioFechas?.inicio || undefined}
              onChange={(e) => setCambioFechas((prev) => (prev ? { ...prev, fin: e.target.value } : prev))}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </label>
        </div>
        {cambioFechas?.inicio && cambioFechas?.fin && cambioFechas.fin >= cambioFechas.inicio && (
          <p className="text-sm text-slate-600">{diasEntre(cambioFechas.inicio, cambioFechas.fin)} día(s) naturales</p>
        )}
        <AvisoEventos eventos={cambioFechas ? eventosEnRango(presupuestos, cambioFechas.inicio, cambioFechas.fin) : []} fuerte />
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!solABorrar}
        titulo="Eliminar solicitud"
        descripcion={solABorrar ? `Se eliminará la ausencia de ${nombreEmpleado(solABorrar.empleado_id)} (${fmtDia(solABorrar.fecha_inicio)} a ${fmtDia(solABorrar.fecha_fin)}). No se puede deshacer.` : ''}
        textoConfirmar="Eliminar"
        peligro
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarBorrarSolicitud}
        onCancelar={cerrarDialogos}
        testId="dialogo-borrar-vac"
      />

      <DialogoConfirmar
        abierto={!!nuevaAusencia}
        titulo="Añadir ausencia"
        descripcion="Queda aprobada directamente y se ve en el calendario de eventos."
        textoConfirmar="Guardar ausencia"
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarNuevaAusencia}
        onCancelar={cerrarDialogos}
        testId="dialogo-nueva-ausencia"
      >
        <label className="block text-sm font-medium text-slate-700">
          Empleado
          <select
            value={nuevaAusencia?.empleado_id || ''}
            onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, empleado_id: e.target.value } : prev))}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
            data-testid="nueva-ausencia-empleado"
          >
            <option value="">Selecciona empleado...</option>
            {todosEmpleados.filter((e) => e.activo !== false && e.nombre?.toLowerCase() !== 'admin').map((e) => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium text-slate-700" data-guia={esDireccion ? 'ausencia-dia-ocupado' : undefined}>
          Tipo
          <select
            value={nuevaAusencia?.tipo || 'vacaciones'}
            onChange={(e) => {
              const tipo = e.target.value;
              // Día ocupado: por defecto un solo día (fin = inicio)
              setNuevaAusencia((prev) => (prev ? {
                ...prev,
                tipo,
                ...(tipo === 'dia_ocupado' && prev.fecha_inicio ? { fecha_fin: prev.fecha_inicio } : {}),
              } : prev));
            }}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
            data-testid="nueva-ausencia-tipo"
          >
            {TIPOS_AUSENCIA.filter((t) => !t.soloDireccion || esDireccion).map((t) => (
              <option key={t.valor} value={t.valor}>{t.opcion || t.texto}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-slate-700">
            Desde
            <input
              type="date"
              value={nuevaAusencia?.fecha_inicio || ''}
              onChange={(e) => {
                const valor = e.target.value;
                setNuevaAusencia((prev) => (prev ? {
                  ...prev,
                  fecha_inicio: valor,
                  // Día ocupado: la fecha fin sigue a la de inicio
                  ...(prev.tipo === 'dia_ocupado' && (!prev.fecha_fin || prev.fecha_fin === prev.fecha_inicio || prev.fecha_fin < valor) ? { fecha_fin: valor } : {}),
                } : prev));
              }}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Hasta
            <input
              type="date"
              value={nuevaAusencia?.fecha_fin || ''}
              min={nuevaAusencia?.fecha_inicio || undefined}
              onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, fecha_fin: e.target.value } : prev))}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </label>
        </div>
        {nuevaAusencia?.fecha_inicio && nuevaAusencia?.fecha_fin && nuevaAusencia.fecha_fin >= nuevaAusencia.fecha_inicio && (
          <p className="text-sm text-slate-600">
            {diasEntre(nuevaAusencia.fecha_inicio, nuevaAusencia.fecha_fin)} día(s) naturales
            {nuevaAusencia.tipo === 'vacaciones' && ', se descontarán del saldo anual'}
            {nuevaAusencia.tipo === 'dia_ocupado' && ', no descuenta vacaciones'}
          </p>
        )}
        {nuevaAusencia?.tipo === 'dia_ocupado' && (
          <>
            <label className="block text-sm font-medium text-slate-700">
              Motivo del día ocupado
              <select
                value={nuevaAusencia?.motivo_ocupado || 'medico'}
                onChange={(e) => {
                  const valor = e.target.value;
                  setNuevaAusencia((prev) => (prev ? { ...prev, motivo_ocupado: valor } : prev));
                }}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                data-testid="nueva-ausencia-motivo-ocupado"
              >
                {MOTIVOS_OCUPADO.map((m) => (
                  <option key={m.valor} value={m.valor}>{m.opcion}</option>
                ))}
              </select>
            </label>
            {nuevaAusencia?.motivo_ocupado === 'otro' && (
              <label className="block text-sm font-medium text-slate-700">
                Cuál
                <input
                  type="text"
                  value={nuevaAusencia?.motivo_texto || ''}
                  maxLength={120}
                  onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, motivo_texto: e.target.value } : prev))}
                  placeholder="Ej: mudanza"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                  data-testid="nueva-ausencia-motivo-texto"
                />
              </label>
            )}
          </>
        )}
        {nuevaAusencia?.tipo === 'dia_ocupado' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-slate-700">
              Hora (opcional)
              <input
                type="time"
                value={nuevaAusencia?.hora || ''}
                onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, hora: e.target.value } : prev))}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="nueva-ausencia-hora"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              Lugar (opcional)
              <input
                type="text"
                value={nuevaAusencia?.lugar || ''}
                maxLength={200}
                onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, lugar: e.target.value } : prev))}
                placeholder="Ej: Geseme"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="nueva-ausencia-lugar"
              />
            </label>
          </div>
        )}
        {nuevaAusencia?.tipo === 'dia_ocupado' && (
          <p className="text-xs text-slate-500">Las ausencias solo las ven la propia persona y dirección. Nadie más.</p>
        )}
        <AvisoEventos eventos={nuevaAusencia?.fecha_inicio && nuevaAusencia?.fecha_fin ? eventosEnRango(presupuestos, nuevaAusencia.fecha_inicio, nuevaAusencia.fecha_fin) : []} fuerte />
        <label className="block text-sm font-medium text-slate-700">
          Motivo (opcional)
          <input
            type="text"
            value={nuevaAusencia?.motivo || ''}
            onChange={(e) => setNuevaAusencia((prev) => (prev ? { ...prev, motivo: e.target.value } : prev))}
            placeholder="Ej: vacaciones de verano"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
        </label>
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!cierreABorrar}
        titulo="Eliminar periodo de cierre"
        descripcion={cierreABorrar ? `Se eliminará "${cierreABorrar.nombre}" (${fmtDate(cierreABorrar.fecha_inicio)} - ${fmtDate(cierreABorrar.fecha_fin)}).` : ''}
        textoConfirmar="Eliminar"
        peligro
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarBorrarCierre}
        onCancelar={cerrarDialogos}
        testId="dialogo-borrar-cierre"
      />
    </div>
  );
}
